import { HttpErrorResponse } from '@angular/common/http';
import { CognitoUser, PasswordResetTokenStatus } from '@cco/model';
import {
  AdminPasswordReset,
  AdminPasswordResetFailure,
  AdminPasswordResetSuccess,
  AuthActions,
  GetPasswordResetTokenStatus,
  GetPasswordResetTokenStatusFailure,
  GetPasswordResetTokenStatusSuccess,
  LoginPingSuccess,
  LoginRedirect,
  LoginSuccess,
  Logout,
  ResetPassword,
  ResetPasswordFailure,
  ResetPasswordSuccess,
  XsrfPing,
  XsrfPingFailure,
  XsrfPingSuccess,
} from '../actions/auth';

export interface State {
  sendingPing: boolean;
  lastPingRtts: number[];
  loginPingCompleted: boolean;
  postLoginRedirectUrl: string;
  loggedIn: boolean;
  user: CognitoUser | null;
  passwordResetTokenStatus: PasswordResetTokenStatus | null;
  passwordResetSuccessful: { [token: string]: boolean };
  errors: {
    ADMIN_PASSWORD_RESET: HttpErrorResponse;
    GET_PASSWORD_RESET_TOKEN_STATUS: HttpErrorResponse;
    RESET_PASSWORD: HttpErrorResponse;
  };
}

export const initialState: State = {
  sendingPing: false,
  lastPingRtts: [],
  loginPingCompleted: false,
  postLoginRedirectUrl: null,
  loggedIn: false,
  user: null,
  passwordResetTokenStatus: null,
  passwordResetSuccessful: {},
  errors: {
    ADMIN_PASSWORD_RESET: null,
    GET_PASSWORD_RESET_TOKEN_STATUS: null,
    RESET_PASSWORD: null,
  },
};

export function reducer(state: State = initialState, action: typeof AuthActions): State {
  switch (action.type) {
    case AdminPasswordReset.type: {
      return {
        ...state,
        errors: { ...state.errors, ADMIN_PASSWORD_RESET: null },
      };
    }

    case AdminPasswordResetSuccess.type: {
      return {
        ...state,
        errors: { ...state.errors, ADMIN_PASSWORD_RESET: null },
      };
    }

    case AdminPasswordResetFailure.type: {
      return {
        ...state,
        errors: { ...state.errors, ADMIN_PASSWORD_RESET: action.error },
      };
    }

    case GetPasswordResetTokenStatus.type: {
      return {
        ...state,
        errors: { ...state.errors, GET_PASSWORD_RESET_TOKEN_STATUS: null },
        passwordResetTokenStatus: null,
      };
    }

    case GetPasswordResetTokenStatusSuccess.type: {
      return {
        ...state,
        errors: { ...state.errors, GET_PASSWORD_RESET_TOKEN_STATUS: null },
        passwordResetTokenStatus: action.status,
      };
    }

    case GetPasswordResetTokenStatusFailure.type: {
      return {
        ...state,
        errors: { ...state.errors, GET_PASSWORD_RESET_TOKEN_STATUS: action.error },
        passwordResetTokenStatus: null,
      };
    }

    case LoginSuccess.type: {
      return {
        ...state,
        loggedIn: true,
        user: action.user,
      };
    }

    case LoginPingSuccess.type: {
      return {
        ...state,
        loggedIn: true,
        user: action.user,
      };
    }

    case LoginRedirect.type: {
      // Do not allow redirects back to the login page
      if ('/login' === action.postLoginRedirectUrl) {
        return state;
      } else {
        return {
          ...state,
          postLoginRedirectUrl: action.postLoginRedirectUrl,
        };
      }
    }

    case Logout.type: {
      return { ...state, loggedIn: initialState.loggedIn, user: initialState.user };
    }

    case ResetPassword.type: {
      return {
        ...state,
        errors: { ...state.errors, RESET_PASSWORD: null },
      };
    }

    case ResetPasswordSuccess.type: {
      const success = {};
      success[action.token] = true;
      return {
        ...state,
        errors: { ...state.errors, RESET_PASSWORD: null },
        passwordResetSuccessful: { ...state.passwordResetSuccessful, ...success },
      };
    }

    case ResetPasswordFailure.type: {
      return {
        ...state,
        errors: { ...state.errors, RESET_PASSWORD: action.error },
      };
    }

    case XsrfPing.type: {
      return { ...state, sendingPing: true };
    }

    case XsrfPingSuccess.type: {
      return {
        ...state,
        sendingPing: false,
        lastPingRtts: [action.rtt, ...(state.lastPingRtts ? state.lastPingRtts.slice(0, 24) : [])],
      };
    }

    case XsrfPingFailure.type: {
      return {
        ...state,
        sendingPing: false,
        lastPingRtts: [-1, ...(state.lastPingRtts ? state.lastPingRtts.slice(0, 24) : [])],
      };
    }

    default: {
      return state;
    }
  }
}

export const getSendingPing = (state: State) => state.sendingPing;
export const getLastPingRtts = (state: State) => state.lastPingRtts;
export const getPostLoginRedirectUrl = (state: State) => state.postLoginRedirectUrl;
export const getLoggedIn = (state: State) => state.loggedIn;
export const getUser = (state: State) => state.user;
export const getLoginPingCompleted = (state: State) => state.loginPingCompleted;
export const getUsernameByResetToken = (state: State) => state.passwordResetTokenStatus;
